export type DateInput = Date | string | number;

export type PointInTimeParts = {
  date: string;
  time: string;
};

export type DateFormats = {
  pointInTime: (value: DateInput) => string;
  pointInTimeParts: (value: DateInput) => PointInTimeParts;
  pointInTimeWithAge: (value: DateInput) => string;
  pointInTimeWithOffset: (value: DateInput) => string;
  recentAge: (value: DateInput) => string | null;
  fullTimestamp: (value: DateInput) => string;
  dayOfPointInTime: (value: DateInput) => string;
  shortDayOfPointInTime: (value: DateInput) => string;
  hourOfPointInTime: (value: DateInput) => string;
  monthOfPointInTime: (value: DateInput) => string;
  calendarDay: (value: DateInput) => string;
  shortCalendarDay: (value: DateInput) => string;
  calendarMonth: (value: DateInput) => string;
  calendarMonthName: (value: DateInput) => string;
  relativeAge: (value: DateInput) => string;
};

type DateFormatsOptions = {
  locale: string;
  timeZone?: string;
  now?: () => Date;
};

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const RECENT_AGE_MS = 7 * DAY_MS;
const JUST_NOW = 'just now';

const RELATIVE_UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['day', DAY_MS],
  ['hour', HOUR_MS],
  ['minute', MINUTE_MS],
];

export const NO_POINT_IN_TIME = '---';

// Queue-service writes the Unix epoch as next_cache_at for priority-1 recaches, meaning "no time".
export function isPointInTime(value: DateInput | null | undefined): value is DateInput {
  if (!value) return false;
  const time = new Date(value).getTime();
  return time !== 0 && !Number.isNaN(time);
}

const orPlaceholder =
  (format: (value: DateInput) => string) =>
  (value: DateInput): string =>
    isPointInTime(value) ? format(value) : NO_POINT_IN_TIME;

// The raw browser locale would put `12. Aug. 2026` and `vor 6 Tagen` inside an English UI.
// A region with no `en-<region>` locale in the browser's ICU data (Japan, Brazil, Mexico…)
// resolves to plain `en`, so those users get US ordering and a 12-hour clock.
export function displayLocale(language: string): string {
  try {
    const region = new Intl.Locale(language).maximize().region ?? 'US';
    return `en-${region}`;
  } catch {
    // Some webviews report an empty or malformed `navigator.language`, which `Intl.Locale` rejects.
    return 'en-US';
  }
}

export function createDateFormats({ locale, timeZone, now = () => new Date() }: DateFormatsOptions): DateFormats {
  const dayParts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric', timeZone };
  const dayFormat = new Intl.DateTimeFormat(locale, dayParts);
  const calendarDayFormat = new Intl.DateTimeFormat(locale, { ...dayParts, timeZone: 'UTC' });
  const hasDayPeriod = new Intl.DateTimeFormat(locale, { hour: 'numeric' })
    .formatToParts(new Date())
    .some((part) => part.type === 'dayPeriod');
  const hour = hasDayPeriod ? 'numeric' : '2-digit';
  const dateTimeFormat = new Intl.DateTimeFormat(locale, { ...dayParts, hour, minute: '2-digit' });
  const shortDayFormat = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone });
  const shortCalendarDayFormat = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  // A bare `14` on a 24-hour axis reads as a day of the month.
  const hourFormat = new Intl.DateTimeFormat(locale, {
    hour,
    minute: hasDayPeriod ? undefined : '2-digit',
    timeZone,
  });
  const monthFormat = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone });
  const calendarMonthFormat = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric', timeZone: 'UTC' });
  const calendarMonthNameFormat = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
  const offsetFormat = new Intl.DateTimeFormat(locale, {
    ...dayParts,
    hour,
    minute: '2-digit',
    timeZoneName: 'shortOffset',
  });
  const fullFormat = new Intl.DateTimeFormat(locale, {
    ...dayParts,
    hour,
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'shortOffset',
  });
  const relativeFormat = new Intl.RelativeTimeFormat('en', { numeric: 'always' });

  // ICU 78 (Node 24.21) writes a zero offset as `GMT+0`; older ICU, and so older browsers, write `GMT`.
  const withOffset = (format: Intl.DateTimeFormat, value: DateInput): string =>
    format.format(new Date(value)).replace(/GMT\+0$/, 'GMT');

  const msFromNow = (value: DateInput) => new Date(value).getTime() - now().getTime();

  function relativeAge(value: DateInput): string {
    const ms = msFromNow(value);
    // Also absorbs a client clock running a few seconds behind the server, which would read "in 30 seconds".
    if (Math.abs(ms) < MINUTE_MS) return JUST_NOW;
    const [unit, unitMs] = RELATIVE_UNITS.find(([, size]) => Math.abs(ms) >= size) ?? ['minute', MINUTE_MS];
    return relativeFormat.format(Math.trunc(ms / unitMs), unit);
  }

  function pointInTime(value: DateInput): string {
    return dateTimeFormat.format(new Date(value));
  }

  function pointInTimeParts(value: DateInput): PointInTimeParts {
    const parts = dateTimeFormat.formatToParts(new Date(value));
    const hourAt = parts.findIndex((part) => part.type === 'hour');
    // V8 swaps ICU's narrow no-break space before AM/PM for a plain one in `format` but not here.
    const join = (slice: Intl.DateTimeFormatPart[]) =>
      slice
        .map((part) => part.value)
        .join('')
        .replace(/\u202f/g, ' ');
    return { date: join(parts.slice(0, hourAt)).trimEnd(), time: join(parts.slice(hourAt)) };
  }

  function recentAge(value: DateInput): string | null {
    return isPointInTime(value) && Math.abs(msFromNow(value)) < RECENT_AGE_MS ? relativeAge(value) : null;
  }

  return {
    pointInTime: orPlaceholder(pointInTime),
    pointInTimeParts,
    relativeAge,
    recentAge,
    pointInTimeWithAge: orPlaceholder((value) => {
      const age = recentAge(value);
      return age ? `${pointInTime(value)} (${age})` : pointInTime(value);
    }),
    pointInTimeWithOffset: orPlaceholder((value) => withOffset(offsetFormat, value)),
    fullTimestamp: orPlaceholder((value) => withOffset(fullFormat, value)),
    dayOfPointInTime: orPlaceholder((value) => dayFormat.format(new Date(value))),
    shortDayOfPointInTime: orPlaceholder((value) => shortDayFormat.format(new Date(value))),
    hourOfPointInTime: orPlaceholder((value) => hourFormat.format(new Date(value))),
    monthOfPointInTime: orPlaceholder((value) => monthFormat.format(new Date(value))),
    calendarDay: orPlaceholder((value) => calendarDayFormat.format(new Date(value))),
    shortCalendarDay: orPlaceholder((value) => shortCalendarDayFormat.format(new Date(value))),
    calendarMonth: orPlaceholder((value) => calendarMonthFormat.format(new Date(value))),
    calendarMonthName: orPlaceholder((value) => calendarMonthNameFormat.format(new Date(value))),
  };
}

export const DISPLAY_LOCALE = displayLocale(navigator.language);

export const dates: DateFormats = createDateFormats({ locale: DISPLAY_LOCALE });
